import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@pinia/colada';
import { roleHelpers } from '@/services/utils';
import { filterableCustomFields } from '@/utils/customFieldFilterHelpers';
import type { FilterableCustomField } from '@/models/customFieldFilterTypes';
import ClientsListService from '../services/ClientsListService';
import { buildClientsTableParams } from '../utils/clientsListHelpers';
import type {
  ClientRow,
  ClientsFilters,
  ClientsTableParams,
  ScopeAccount,
} from '../models/clientsListTypes';

/**
 * Colada is an internal request wrapper here; the page and its components only read
 * this store's refs and call its actions. The filters live here too, so the filter
 * panel and the table share them without mutating each other's props.
 *
 * The table query is `staleTime: 0` + `.refetch(true)` - the list must be live - and
 * keyed off the exact params of each load, which loadClients() sets just before
 * refetching. Two loads with the same params share an entry and Colada aborts the
 * older one; the service doesn't forward that signal, so the older call resolves with
 * the entry's current state instead of rejecting. The token guard stops a superseded
 * load from writing rows or clearing `loading`, and nothing reads `state.data` without
 * a fallback.
 *
 * Custom field definitions rarely change, so that query has a real staleTime and is
 * driven with `.refresh(true)`.
 */
const CUSTOM_FIELDS_STALE_TIME = 5 * 60 * 1000;

const getDefaultFilters = (): ClientsFilters => ({
  search: '',
  scopeAccount: null,
  originating: false,
  primary: false,
  reviewer: false,
  orphans: false,
  checkQuickbooks: false,
  qbConnectionType: 'any',
  clientCustomFields: {},
  page: 1,
  pageSize: 20,
  orderBy: 'clientId',
  sortAscending: true,
});

export const useClientsListStore = defineStore('clientsList', () => {
  const filters = ref<ClientsFilters>(getDefaultFilters());
  const clients = ref<ClientRow[]>([]);
  const totalCount = ref(0);
  const customFields = ref<FilterableCustomField[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const error = ref<unknown>(null);

  const queryParams = computed(() => buildClientsTableParams(filters.value));

  const activeFilterCount = computed(() => {
    const f = filters.value;
    return [
      f.search,
      f.scopeAccount,
      f.orphans,
      f.checkQuickbooks && f.qbConnectionType !== 'any',
      Object.values(f.clientCustomFields).some(Boolean),
    ].filter(Boolean).length;
  });

  const tableParams = ref<ClientsTableParams>({});

  const tableQuery = useQuery({
    key: () => ['clients-list', 'table', tableParams.value],
    query: () => ClientsListService.fetchTable(tableParams.value),
    enabled: false,
    staleTime: 0,
  });

  const customFieldsQuery = useQuery({
    key: () => ['clients-list', 'custom-fields'],
    query: () => ClientsListService.fetchCustomFields(),
    enabled: false,
    staleTime: CUSTOM_FIELDS_STALE_TIME,
  });

  const deleteMutation = useMutation({
    mutation: (clientId: string) => ClientsListService.deleteClient(clientId),
  });

  let loadToken = 0;

  async function loadClients(): Promise<void> {
    const token = ++loadToken;
    tableParams.value = queryParams.value;
    loading.value = true;
    error.value = null;
    try {
      const state = await tableQuery.refetch(true);
      if (token !== loadToken) return;
      clients.value = state.data?.items ?? [];
      totalCount.value = state.data?.count ?? 0;
    } catch (e) {
      if (token !== loadToken) return;
      error.value = e;
      throw e;
    } finally {
      if (token === loadToken) loading.value = false;
    }
  }

  /**
   * Every row matching the current filters, regardless of page, for export. Sized by the
   * last load's count, so the page keeps Export disabled while a reload is in flight.
   */
  async function fetchAllClients(): Promise<ClientRow[]> {
    exporting.value = true;
    try {
      const page = await ClientsListService.fetchTable({
        ...queryParams.value,
        limit: totalCount.value,
        offset: 0,
      });
      return page.items ?? [];
    } finally {
      exporting.value = false;
    }
  }

  async function loadCustomFields(): Promise<void> {
    const state = await customFieldsQuery.refresh(true);
    customFields.value = filterableCustomFields(state.data ?? [], 'client');
  }

  const deleteClient = (clientId: string): Promise<void> => deleteMutation.mutateAsync(clientId);

  /**
   * The legacy page's starting scope (matterFilterService): operators and accountants
   * see every client; everyone else sees clients involving them, with Responsible
   * ticked for leads and principals.
   */
  function applyRoleDefaults(role: string | undefined, account: ScopeAccount | undefined): void {
    if (!role || !account || roleHelpers.isOperatorOrAccountantRole(role)) return;
    filters.value.scopeAccount = account;
    filters.value.primary = role === 'lead' || role === 'principal';
  }

  function resetFilters(): void {
    filters.value = getDefaultFilters();
  }

  /** On leaving the page: client names and phone numbers shouldn't outlive it in a singleton. */
  function reset(): void {
    loadToken += 1;
    filters.value = getDefaultFilters();
    clients.value = [];
    totalCount.value = 0;
    loading.value = false;
    error.value = null;
  }

  return {
    filters,
    clients,
    totalCount,
    customFields,
    loading,
    exporting,
    error,
    queryParams,
    activeFilterCount,
    loadClients,
    fetchAllClients,
    loadCustomFields,
    deleteClient,
    applyRoleDefaults,
    resetFilters,
    reset,
  };
});
